import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import {
  ITimesheetData
} from "../../../../store/timesheet/types";
import ReviewTimesheet from "../../Timesheet/ReviewTimesheet";
import PopupModal from "../../../UI/Popup";
import { TSuccessMsg } from "../../Timesheet";
import { useAppDispatch as useDispatch } from "../../../../store";
import { showNotificationAction } from "../../../../store/application/slice";

interface IPendingTimesheetApprovalProps {
  clientPendingTimesheetList: ITimesheetData[]; // multiple mode
  setShowRatingPopup: Dispatch<SetStateAction<boolean>>;
  setIsShowPendingTimesheetNotification: Dispatch<SetStateAction<boolean>>;
}

const PendingTimesheetApproval: FC<IPendingTimesheetApprovalProps> = ({
  clientPendingTimesheetList,
  setShowRatingPopup,
  setIsShowPendingTimesheetNotification
}) => {
  const dispatch = useDispatch();

  const [showPendingTimesheetApprovalPopup, setShowPendingTimesheetApprovalPopup] = useState(false);
  const [currentTimesheetIndex, setCurrentTimesheetIndex] = useState(0);
  const [currentTimesheet, setCurrentTimesheet] = useState<ITimesheetData | null>(null);

  const totalPendingTimesheetCount = clientPendingTimesheetList.length;
  const isLastTimesheet = currentTimesheetIndex === totalPendingTimesheetCount - 1;
  const hasMoreTimesheets = currentTimesheetIndex < totalPendingTimesheetCount - 1;

  // Update current timesheet when index or list changes
  useEffect(() => {
    if(clientPendingTimesheetList.length > 0) {
      setShowPendingTimesheetApprovalPopup(true);
      setIsShowPendingTimesheetNotification(true)
    } else {
      setIsShowPendingTimesheetNotification(false)
    }

    setCurrentTimesheet(clientPendingTimesheetList?.[currentTimesheetIndex] ?? null);
  }, [currentTimesheetIndex, clientPendingTimesheetList, setIsShowPendingTimesheetNotification]);

  const onClick = (isUserInitiated: boolean) => {
    setCurrentTimesheet(null);
    setShowPendingTimesheetApprovalPopup(false);

    if (isUserInitiated) {
      if (isLastTimesheet) {
        // ✅ Condition 1: Last timesheet + user clicked — show rating popup immediately
        setShowRatingPopup(true);
      } else if (hasMoreTimesheets) {
        // ✅ Condition 2: More timesheets + user clicked — show rating popup immediately
        setShowRatingPopup(true);
      } else {
        // ✅ Condition 3: Fallback — show rating popup
        setShowRatingPopup(true);
      }
    } else {
      if (isLastTimesheet) {
        // ✅ Condition 4: Last timesheet + NOT user-initiated — show rating popup after delay
        setTimeout(() => {
          setShowRatingPopup(true);
        }, 3000);
      } else if (hasMoreTimesheets) {
        // ✅ Condition 5: More timesheets + NOT user-initiated — go to next timesheet
        setTimeout(() => {
          setShowPendingTimesheetApprovalPopup(true);
        }, 3000);
      } else {
        // ✅ Condition 6: Fallback — show rating popup
        setShowRatingPopup(true);
      }
    }
  };

  const handleActionSuccess = (msg: TSuccessMsg) => {
    dispatch(showNotificationAction(msg ?? ""))
    setTimeout(() => {
      if (currentTimesheetIndex < totalPendingTimesheetCount - 1) {
        setCurrentTimesheetIndex(currentTimesheetIndex + 1);
        setShowPendingTimesheetApprovalPopup(true); // Show modal again for next timesheet
      } else {
        setIsShowPendingTimesheetNotification(false)
      }
    }, 3000);
  };

  return (
    <PopupModal
      visible={!!showPendingTimesheetApprovalPopup}
      showCloseBtn={true}
      onClickClose={() => onClick(true)}
      popUpClass="mobile-model"
      customWidth={650}
    >
      <div className="d-flex flex-column gap-20">
        {currentTimesheet && (
          <ReviewTimesheet
            timesheet={currentTimesheet}
            onActionSuccess={handleActionSuccess}
            onClickClose={() => onClick(false)}
            type={"index"}
            count={totalPendingTimesheetCount}
            currentIndex={currentTimesheetIndex}
          />
        )}
      </div>
    </PopupModal>
  );
};

export default PendingTimesheetApproval;
